import { useEffect } from "react";
import { LastPlaceTracker } from "./lib/lastPlace";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { PublicEnquiry } from "./pages/PublicEnquiry";
import { BuyPlans } from "./pages/BuyPlans";
import { CampaignJoin } from "./pages/CampaignJoin";
import { FamilyJoin } from "./pages/FamilyJoin";
import { Unsubscribe } from "./pages/Unsubscribe";
import { PurchaseWelcome } from "./pages/PurchaseWelcome";
import { GuestLanding } from "./pages/GuestLanding";
import { Workspace } from "./pages/Workspace";
import { GroupWorkspace } from "./pages/GroupWorkspace";
import { AccountSettings } from "./pages/AccountSettings";
import { Builder } from "./pages/Builder";
import { Header } from "./components/chrome/Header";
import { FeedbackDockProvider } from "./components/chrome/FeedbackDock";
import { MiloDock } from "./components/chrome/MiloDock";
import { GuestBanner } from "./components/chrome/GuestBanner";
import { LockedScreen } from "./components/LockedScreen";
import { AccessEnded } from "./components/AccessEnded";
import { kioskNav } from "./lib/kiosk";
import { startRealtime, stopRealtime } from "./realtime/connection";

export function App() {
  const { me, loading, reconnecting, lock, accessEnded } = useAuth();
  const location = useLocation();
  const nav = kioskNav(me);

  // Hold one hub connection open while signed in; drop it on logout. Driven off auth state with no
  // unmount cleanup, so React StrictMode's dev-only remount can't tear the shared connection down
  // mid-handshake (which left component subscriptions stranded).
  useEffect(() => {
    if (me) {
      void startRealtime();
    } else {
      void stopRealtime();
    }
  }, [me]);

  if (loading) {
    return (
      <main style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", color: "var(--text-muted)", textAlign: "center", padding: 24 }}>
        {reconnecting ? "Can't reach KeyDials just now — it may be updating. Trying again…" : "Loading…"}
      </main>
    );
  }

  // A guest invite link (KEY-297) is its own front door: it must render before any auth gating, because
  // its visitor typically has no session yet — and any session they DO have (a locked kiosk included) is
  // simply replaced by the guest one on verify.
  if (location.pathname.startsWith("/guest/")) {
    return <GuestLanding />;
  }

  // The public enquiry page (KEY-313) is the other sessionless front door: an external website's
  // Contact Us link lands here, and the visitor never signs in at all.
  if (location.pathname.startsWith("/enquire/")) {
    return <PublicEnquiry />;
  }

  // The buy page and the page Stripe returns to (KEY-368) are the third sessionless front door. Somebody
  // choosing a plan has no account yet by definition, and the buyer coming back from Stripe still has
  // none — their organisation is created by the webhook, not by this redirect.
  if (location.pathname.startsWith("/billing/plans")) {
    return <BuyPlans />;
  }

  if (location.pathname.startsWith("/billing/welcome")) {
    return <PurchaseWelcome />;
  }

  // A campaign's QR code (KEY-432) is the fourth sessionless front door. Somebody who has just scanned a
  // code at a stand has no account by definition, and any session they DO have is beside the point —
  // this page is about starting a new one.
  if (location.pathname.startsWith("/join/")) {
    return <CampaignJoin />;
  }

  // A family-pack invitation (KEY-651) is the sixth sessionless front door: the relative has no account until
  // they accept, and accepting is what creates it. Whatever session this browser holds is somebody else's
  // business — the page signs its own visitor in with a code sent to the invited address.
  if (location.pathname.startsWith("/family/join/")) {
    return <FamilyJoin />;
  }

  // A drip email's one-click unsubscribe (KEY-433): same sessionless rule as /join/ — the click comes
  // from an inbox, and whatever session this browser holds belongs to somebody beside the point.
  if (location.pathname.startsWith("/unsubscribe/")) {
    return <Unsubscribe />;
  }

  // A locked shared device (KEY-268) outranks everything: no header, no routes, nothing but the lock
  // screen. Checked before `me` because locking clears the session — the device holds a ticket, not a user.
  if (lock) {
    return <LockedScreen />;
  }

  if (!me) {
    return <Login />;
  }

  // A card-free trial that ran out (KEY-431) — or, since KEY-911, a cancelled subscription — closes the app
  // behind the account. Checked here, above every route, for the same reason the lock screen is: there is nothing in the app they can reach, so offering
  // routes would only produce a screenful of 402s. The server refuses those routes itself — this exists so
  // the refusal is explained rather than experienced as a broken app, and so the way back is on screen.
  // `accessEnded` is /api/me's word at sign-in OR a 402 met since (KEY-637), so a trial that runs out while a
  // dial is open lands here too, rather than as an error with no button on whatever page was showing.
  if (accessEnded) {
    return <AccessEnded />;
  }

  // A PIN-unlocked session may reach exactly one dial. The server enforces that on every request; here we
  // simply stop offering the doors — no My Dials, no group workspace, and any stray URL lands back on the
  // one dial rather than on a page that would only 403.
  if (nav.locked && nav.instancePath) {
    return (
      <FeedbackDockProvider>
        <Header />
        {nav.kind === "guest" && <GuestBanner />}
        <main>
          <Routes>
            {/* A literal path, not /instances/:id — another dial's URL must not even mount the workspace
                and fire a request the server will only refuse. The id therefore has to be handed over as a
                prop: useParams() has nothing to give on a route with no parameter in it. */}
            <Route path={nav.instancePath} element={<Workspace instanceId={nav.instanceId ?? undefined} />} />
            <Route path="*" element={<Navigate to={nav.instancePath} replace />} />
          </Routes>
        </main>
      </FeedbackDockProvider>
    );
  }

  // The dial builder (KEY-560) is a full screen of its own, outside the header: the header has no width to spare, and every link
  // in it would be a way out that skips saving. Checked after the lock, the trial and the shared-device session, so none of those
  // can ever reach it. The feedback dock still comes, because the builder is exactly where someone finds what it can't do.
  if (location.pathname === "/build" || location.pathname.startsWith("/build/")) {
    return (
      <FeedbackDockProvider>
        <Routes>
          <Route path="/build/new" element={<Builder />} />
          <Route path="/build/:id" element={<Builder />} />
          <Route path="*" element={<Navigate to="/build/new" replace />} />
        </Routes>
      </FeedbackDockProvider>
    );
  }

  return (
    <FeedbackDockProvider>
      <Header />
      <LastPlaceTracker />
      {/* Milo (KEY-704): on every ordinary page, and only here — a PIN-unlocked or guest session cannot reach
          /api/milo, and the builder has its own screen. Renders nothing where Features:Milo is off. */}
      <MiloDock />
      <main>
        <Routes>
          <Route path="/instances" element={<Dashboard />} />
          <Route path="/instances/:id" element={<Workspace />} />
          {/* KEY-455: settings render inside Workspace rather than a page of their own. Workspace owns the
              cross-org scoping, the loader every settings write calls back into, and the kiosk idle lock —
              a shared tablet must still lock while sitting on a settings page. */}
          <Route path="/instances/:id/settings" element={<Workspace />} />
          <Route path="/instances/:id/settings/:section" element={<Workspace />} />
          <Route path="/groups/:id" element={<GroupWorkspace />} />
          {/* Your own account, as opposed to a dial's settings — different question, different page. */}
          <Route path="/settings" element={<AccountSettings />} />
          <Route path="*" element={<Navigate to="/instances" replace />} />
        </Routes>
      </main>
    </FeedbackDockProvider>
  );
}
